import React from 'react';
import ReactDomServer from 'react-dom/server';
import { CheckCircleIcon, WarningIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Center, Flex, Text } from '@chakra-ui/react';

import { ItemsSummaryStatusText } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryStatusText';
import { TimedProgress } from '../../../../common/components/TimedProgress/TimedProgress';
import { useHtmlTranslations, useTranslations } from '../../../../contexts/LocalizationContext';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';

export type AssignItemsResultStatus = 'success' | 'partial' | 'failure';

type AssignItemsSuccessBodyProps = {
  status?: AssignItemsResultStatus;
  assignedCount: number;
  totalCount: number;
  userLabel: string;
  orderNumber?: string | null;
  showProgress: boolean;
  onAnimationComplete: () => void;
};

export const AssignItemsSuccessBody = ({
  status = 'success',
  assignedCount,
  totalCount,
  userLabel,
  orderNumber,
  showProgress,
  onAnimationComplete,
}: AssignItemsSuccessBodyProps) => {
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();
  const { isDeliveryNoteEnabled, quickContact } = useScannedRelevosContext();

  const failureCount = Math.max(totalCount - assignedCount, 0);
  const safeUserLabel = userLabel && userLabel.trim().length ? userLabel : '-';

  const statusConfig = {
    success: {
      Icon: CheckCircleIcon,
      color: 'green.800',
      title: translations('assign_items_result_success_title'),
    },
    partial: {
      Icon: WarningTwoIcon,
      color: 'orange.500',
      title: translations('assign_items_result_partial_title'),
    },
    failure: {
      Icon: WarningIcon,
      color: 'red.600',
      title: translations('assign_items_result_failure_title'),
    },
  } as const;

  const { Icon, color, title } = statusConfig[status];
  const summaryText =
    status !== 'success'
      ? translations('assign_items_result_summary_text', {
          '{{successItems}}': assignedCount!.toString(),
          '{{totalItems}}': totalCount!.toString(),
        })
      : null;
  const summarySuccessItems = Array.from({ length: assignedCount });
  const summaryFailureItems = Array.from({ length: failureCount });

  return (
    <Center py={4}>
      <Flex direction="column" align="center" width="100%">
        <Flex align="center">
          <Icon boxSize="20px" color={color} mr={1} my="auto" />
          <Text fontSize={20} fontWeight="bold" p={4} color="grey.900">
            {title}
          </Text>
        </Flex>
        {summaryText ? (
          <Flex pb={6} width="100%">
            <ItemsSummaryStatusText
              summaryText={summaryText}
              successItems={summarySuccessItems}
              failureItems={summaryFailureItems}
            />
          </Flex>
        ) : null}
        {status !== 'failure' && orderNumber ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('assign_items_result_order_confirmed', {
              '{{orderId}}': ReactDomServer.renderToString(<Text as="b">{orderNumber}</Text>),
            })}
          </Text>
        ) : null}
        {status !== 'failure' ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('assign_items_result_order_confirmed_info', {
              '{{items}}': assignedCount!.toString(),
              '{{user}}': ReactDomServer.renderToString(<Text as="b">{safeUserLabel}</Text>),
            })}
          </Text>
        ) : null}
        {status === 'failure' ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('assign_items_result_order_failed')}
          </Text>
        ) : null}
        {status === 'failure' ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('assign_items_result_failure_info', {
              '{{user}}': ReactDomServer.renderToString(<Text as="b">{safeUserLabel}</Text>),
            })}
          </Text>
        ) : null}
        {status !== 'failure' && isDeliveryNoteEnabled && quickContact ? (
          <Text fontSize={20} p={4} align="center">
            {translations('assign_items_result_order_delivery_note_info')}
          </Text>
        ) : null}
        {showProgress ? (
          <TimedProgress show={showProgress} showCountdown={true} onAnimationComplete={onAnimationComplete} />
        ) : null}
      </Flex>
    </Center>
  );
};
